import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useMutation, useApolloClient, useQuery } from '@apollo/client';
import cn from 'classnames';
import * as yup from 'yup';
import { Button, Progress } from 'antd';
// Api
import {
  SUBMIT_DELIVERABLES_FOR_REVIEW_V2,
  CREATE_CAMPAIGN_DELIVERABLES_FILES,
} from 'api/campaignV2/mutations';
// Types
import {
  SubmitDeliverablesForReviewV2,
  SubmitDeliverablesForReviewV2Variables,
} from 'api/campaignV2/types/SubmitDeliverablesForReviewV2';
import {
  CreateCampaignDeliverableFiles,
  CreateCampaignDeliverableFilesVariables,
} from 'api/campaignV2/types/CreateCampaignDeliverableFiles';
import {
  GetCampaignOffers_getCampaignOffers_entities,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  CBDeliverableItemType,
  CbDeliverableItemV2Status,
  CbDeliverableV2Status,
  CampaignMediaType,
  CampaignTaskType,
} from 'api/graphql-global-types';
// Constants
import {
  DONE_DELIVERABLE_STATUSES,
  POST_DELIVERABLE_LINK_TYPES,
} from 'constants/deliverables';
// Helpers
import {
  getSeparatedDeliverableItems,
  deliverableHasContentFileItems,
  isContentOnlyDeliverable,
} from 'helpers/deliverables';
import { uploadDeliverables } from 'helpers/campaign';
import { uploadDeliverableFileWithConversion } from 'helpers/deliverableFileUpload';
import {
  processAllDeliverableFilesWithTranscoding,
  buildSubmitInputFromUploadResults,
  UploadResult,
} from 'helpers/deliverableSubmission';
import { GET_VIDEO_TRANSCODING_JOBS } from 'api/campaignV2/queries';
import {
  GetVideoTranscodingJobs,
  GetVideoTranscodingJobsVariables,
} from 'api/campaignV2/types/GetVideoTranscodingJobs';
import { MediaJobStatus } from 'api/graphql-global-types';
// Components
import SubmitContent from '../SubmitContent/SubmitContent';
import SubmitFinalContent from '../SubmitFinalContent/SubmitFinalContent';
import SubmitCopy from '../SubmitCopy/SubmitCopy';
import DeliverableSubmitted from '../DeliverableSubmitted/DeliverableSubmitted';
// UI
import { successNotification, errorNotification } from 'ui/Notification';
// Styles
import styles from './SubmitContentForm.module.scss';

type FileWithPreview = File & {
  preview: string;
};

type ContentFormData = {
  offerId: string;
  content: {
    deliverableItemId: string;
    files: FileWithPreview[];
  }[];
  finalContent: {
    deliverableItemId: string;
    files: FileWithPreview[];
  }[];
  postCopy: {
    deliverableItemId: string;
    copy: string;
  };
};

const validationSchema = yup.object().shape({
  content: yup.array().of(
    yup.object().shape({
      deliverableItemId: yup.string().when('$allFilesApproved', {
        is: true,
        then: (schema) => schema.optional(),
        otherwise: (schema) =>
          schema.required('Deliverable item id is required'),
      }),
      files: yup.array().when('$allFilesApproved', {
        is: true,
        then: (schema) => schema.optional(),
        otherwise: (schema) =>
          schema.min(1, 'Please upload the content.').of(
            yup
              .mixed()
              .test(
                'is-file',
                'Each item must be a File',
                (value) => value instanceof File
              )
              .required('File is required')
          ),
      }),
    })
  ),
  finalContent: yup.array().of(
    yup.object().shape({
      deliverableItemId: yup.string().when('$allFilesItemsApproved', {
        is: true,
        then: (schema) => schema.optional(),
        otherwise: (schema) =>
          schema.required('Deliverable item id is required'),
      }),
      files: yup.array().when('$allFilesItemsApproved', {
        is: true,
        then: (schema) => schema.optional(),
        otherwise: (schema) =>
          schema.min(1, 'Please upload the final content.').of(
            yup
              .mixed()
              .test(
                'is-file',
                'Each item must be a File',
                (value) => value instanceof File
              )
              .required('File is required')
          ),
      }),
    })
  ),
  postCopy: yup.object().shape({
    deliverableItemId: yup.string().when('$allCopiesApproved', {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.required('Deliverable item id is required'),
    }),
    copy: yup.string().when('$allCopiesApproved', {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) =>
        schema.required('Please write a caption for this post.'),
    }),
  }),
});

type SubmitContentFormProps = {
  offer?: GetCampaignOffers_getCampaignOffers_entities | null;
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
  className?: string;
  formClassName?: string;
  hasPendingRequest?: (deliverableId: string) => boolean;
  onRequestExtension?: (
    deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  ) => void;
  onReviewExtension?: (
    deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  ) => void;
  refetch?: () => void;
};

const SubmitContentForm: React.FC<SubmitContentFormProps> = ({
  offer,
  deliverable,
  className,
  formClassName,
  hasPendingRequest,
  onRequestExtension,
  onReviewExtension,
  refetch,
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<{
    fileName: string;
    progress: number;
    stage: 'uploading' | 'converting';
  } | null>(null);
  const [transcodingState, setTranscodingState] = useState<{
    isUploading: boolean;
    progress: { completed: number; total: number } | null;
    jobIds: string[];
    resultKeys: Map<string, string>;
    error: string | null;
    pendingUploadResults: UploadResult[];
    isComplete: boolean;
  }>({
    isUploading: false,
    progress: null,
    jobIds: [],
    resultKeys: new Map(),
    error: null,
    pendingUploadResults: [],
    isComplete: false,
  });
  const isTranscodingCompleteRef = useRef<boolean>(false);
  const transcodingResultsRef = useRef<Map<string, string>>(new Map());
  const transcodingJobIdsRef = useRef<string[]>([]);

  const { files, copies, filesItems } = getSeparatedDeliverableItems(
    deliverable as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  );

  const nonApprovedFilesItemIds = useMemo(
    () =>
      new Set(
        filesItems
          .filter((item) => item.status !== CbDeliverableItemV2Status.APPROVED)
          .map((item) => item.id)
      ),
    [filesItems]
  );

  const approvedFilesLength = files.filter(
    (item) => item.status === CbDeliverableItemV2Status.APPROVED
  ).length;
  const approvedFilesItemsLength =
    filesItems.length - nonApprovedFilesItemIds.size;
  const approvedCopiesLength = copies.filter(
    (item) => item.status === CbDeliverableItemV2Status.APPROVED
  ).length;

  const isContentApproved =
    deliverable?.status !== undefined &&
    DONE_DELIVERABLE_STATUSES.includes(deliverable.status);
  const isOnlyContentDeliverablesType = isContentOnlyDeliverable(
    offer?.campaign?.type,
    deliverable
  );

  const apolloClient = useApolloClient();
  const campaignId = offer?.campaign?.id || '';

  const [submitDeliverablesForReviewV2] = useMutation<
    SubmitDeliverablesForReviewV2,
    SubmitDeliverablesForReviewV2Variables
  >(SUBMIT_DELIVERABLES_FOR_REVIEW_V2);
  const [createCampaignDeliverableFiles] = useMutation<
    CreateCampaignDeliverableFiles,
    CreateCampaignDeliverableFilesVariables
  >(CREATE_CAMPAIGN_DELIVERABLES_FILES);

  const { data: transcodingData } = useQuery<
    GetVideoTranscodingJobs,
    GetVideoTranscodingJobsVariables
  >(GET_VIDEO_TRANSCODING_JOBS, {
    variables: {
      input: {
        ids: transcodingJobIdsRef.current,
      },
    },
    skip:
      transcodingJobIdsRef.current.length === 0 ||
      isTranscodingCompleteRef.current,
    pollInterval: isTranscodingCompleteRef.current ? 0 : 2000,
    fetchPolicy: 'network-only',
    onError: (error) => {
      setTranscodingState((prev) => ({ ...prev, error: error.message }));
    },
  });

  const methods = useForm<ContentFormData>({
    resolver: yupResolver(validationSchema),
    context: {
      allFilesApproved: approvedFilesLength === files.length,
      allFilesItemsApproved: filesItems.length === approvedFilesItemsLength,
      allCopiesApproved:
        approvedCopiesLength === copies.length || isOnlyContentDeliverablesType,
    },
    defaultValues: {
      content: Array.from(
        {
          length: files.filter(
            (item) => item.status !== CbDeliverableItemV2Status.APPROVED
          ).length,
        },
        () => ({
          deliverableItemId: '',
          files: [],
        })
      ),
      finalContent: Array.from(
        {
          length: filesItems.filter(
            (item) => item.status !== CbDeliverableItemV2Status.APPROVED
          ).length,
        },
        () => ({
          deliverableItemId: '',
          files: [],
        })
      ),
      postCopy: {
        deliverableItemId: '',
        copy: '',
      },
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const resetTranscodingState = () => {
    isTranscodingCompleteRef.current = false;
    transcodingResultsRef.current = new Map();
    transcodingJobIdsRef.current = [];
    setTranscodingState({
      isUploading: false,
      progress: null,
      jobIds: [],
      resultKeys: new Map(),
      error: null,
      pendingUploadResults: [],
      isComplete: false,
    });
  };

  const processTranscodingJobs = (
    jobs: GetVideoTranscodingJobs['getVideoTranscodingJobs']
  ) => {
    const resultKeys = new Map<string, string>();
    const failedJobs: string[] = [];

    jobs.forEach((job) => {
      if (job.status === MediaJobStatus.COMPLETED && job.resultKey) {
        resultKeys.set(job.id, job.resultKey);
      } else if (job.status === MediaJobStatus.FAILED) {
        failedJobs.push(job.id);
      }
    });

    transcodingResultsRef.current = resultKeys;

    const completedCount = resultKeys.size;
    const totalJobs = transcodingJobIdsRef.current.length;

    setTranscodingState((prev) => ({
      ...prev,
      resultKeys,
      progress: {
        completed: completedCount,
        total: totalJobs,
      },
    }));

    if (completedCount > 0 || failedJobs.length > 0) {
      const progress = Math.round((completedCount / totalJobs) * 100);
      setUploadProgress({
        fileName: 'Videos',
        progress,
        stage: 'converting',
      });
    }

    const allJobsDone = completedCount + failedJobs.length === totalJobs;

    if (allJobsDone) {
      isTranscodingCompleteRef.current = true;
      setUploadProgress(null);

      if (failedJobs.length > 0) {
        const errorDetails = failedJobs
          .map((jobId) => {
            const job = jobs.find((j) => j.id === jobId);
            return job
              ? `Job ${job.id}: ${job.errorMessage || 'Unknown error'}`
              : `Job ${jobId}: Unknown error`;
          })
          .join('; ');

        setTranscodingState((prev) => ({
          ...prev,
          isUploading: false,
          progress: null,
          error: `Transcoding failed for ${failedJobs.length} job(s): ${errorDetails}`,
          isComplete: true,
        }));
      } else {
        setTranscodingState((prev) => ({
          ...prev,
          isComplete: true,
        }));
      }
    }

    return { failedJobs, allJobsDone };
  };

  const buildFinalSubmissionItems = useCallback(
    (
      contentTasks: ReturnType<typeof buildSubmitInputFromUploadResults>,
      postCopyData: ContentFormData['postCopy']
    ) => {
      const postTask = {
        deliverableItemId: postCopyData.deliverableItemId as string,
        type: CBDeliverableItemType.COPY,
        copy: postCopyData.copy as string,
      };
      const hasPostTask = Boolean(
        postTask.deliverableItemId && postTask.copy?.trim()
      );

      const isCopyApproved = copies.some(
        (item) =>
          item.id === postTask.deliverableItemId &&
          item.status === CbDeliverableItemV2Status.APPROVED
      );

      const contentToSubmit = contentTasks.filter((task) =>
        files.find(
          (item) =>
            item.id === task.deliverableItemId &&
            item.status !== CbDeliverableItemV2Status.APPROVED
        )
      );

      return {
        finalItems: [
          ...contentToSubmit,
          ...(isCopyApproved || isOnlyContentDeliverablesType || !hasPostTask
            ? []
            : [postTask]),
        ],
      };
    },
    [copies, files, isOnlyContentDeliverablesType]
  );

  const validateTranscodingResults = (
    allUploadResults: Array<{ videoTranscodingJobId?: string | null }>,
    resultKeys: Map<string, string>
  ) => {
    const jobsNeedingTranscoding = allUploadResults
      .filter((item) => item.videoTranscodingJobId)
      .map((item) => item.videoTranscodingJobId as string);

    const missingJobs = jobsNeedingTranscoding.filter(
      (jobId) => !resultKeys.has(jobId)
    );

    if (missingJobs.length > 0) {
      throw new Error(
        `Transcoding incomplete. Missing results for ${
          missingJobs.length
        } job(s): ${missingJobs.join(', ')}`
      );
    }
  };

  const submitDeliverablesAfterTranscoding = useCallback(async () => {
    if (transcodingState.error) {
      return;
    }

    const resultKeys = transcodingResultsRef.current;

    const allUploadResults = transcodingState.pendingUploadResults.flatMap(
      (result) => result.submitInput
    );

    validateTranscodingResults(allUploadResults, resultKeys);

    // Separate regular content uploads from FILES uploads
    const regularUploads = transcodingState.pendingUploadResults.filter(
      (result) =>
        !nonApprovedFilesItemIds.has(result.submitInput[0]?.deliverableItemId)
    );
    const finalContentUploads = transcodingState.pendingUploadResults.filter(
      (result) =>
        nonApprovedFilesItemIds.has(result.submitInput[0]?.deliverableItemId)
    );

    const contentTasks = buildSubmitInputFromUploadResults(
      regularUploads,
      resultKeys
    );

    const postCopyData = methods.getValues('postCopy');
    const { finalItems } = buildFinalSubmissionItems(
      contentTasks,
      postCopyData
    );

    // Build FILES items for final content
    const finalContentToSubmit = finalContentUploads.map((result) => ({
      deliverableItemId: result.submitInput[0]?.deliverableItemId,
      type: CBDeliverableItemType.FILES as CBDeliverableItemType,
      files: result.submitInput.map((input) => {
        const transcodeKey = input.videoTranscodingJobId
          ? resultKeys.get(input.videoTranscodingJobId)
          : undefined;
        return {
          key: transcodeKey ?? input.key,
          name: input.name,
          type: CampaignMediaType.VIDEO,
          ...(input.videoTranscodingJobId
            ? { videoTranscodingJobId: input.videoTranscodingJobId }
            : {}),
        };
      }),
    }));

    await submitDeliverablesForReviewV2({
      variables: {
        input: {
          offerId: offer?.id ?? '',
          items: [...finalItems, ...finalContentToSubmit],
        },
      },
      awaitRefetchQueries: true,
    });

    successNotification('Deliverables submitted for review successfully.');

    setIsUploading(false);
    setUploadProgress(null);
    resetTranscodingState();

    if (refetch) {
      await refetch();
    }
  }, [
    transcodingState.error,
    transcodingState.pendingUploadResults,
    methods,
    buildFinalSubmissionItems,
    nonApprovedFilesItemIds,
    offer,
    refetch,
    submitDeliverablesForReviewV2,
  ]);

  const handleSubmit = () => {
    if (isCollapsed) {
      handleToggleCollapse();
    } else {
      files.forEach((file, index) => {
        if (file.status !== CbDeliverableItemV2Status.APPROVED && file.id) {
          const formIndex = files
            .slice(0, index)
            .filter(
              (f) => f.status !== CbDeliverableItemV2Status.APPROVED
            ).length;
          methods.setValue(`content.${formIndex}.deliverableItemId`, file.id, {
            shouldValidate: false,
          });
        }
      });

      // Set finalContent deliverableItemIds
      const nonApprovedFilesItems = filesItems.filter(
        (item) => item.status !== CbDeliverableItemV2Status.APPROVED
      );
      nonApprovedFilesItems.forEach((filesItem, index) => {
        methods.setValue(
          `finalContent.${index}.deliverableItemId`,
          filesItem.id,
          { shouldValidate: false }
        );
      });

      const copyItem = copies.find(
        (c) => c.type === CBDeliverableItemType.COPY
      );
      if (copyItem?.id) {
        methods.setValue('postCopy.deliverableItemId', copyItem.id, {
          shouldValidate: false,
        });
      }

      methods.handleSubmit(handleFormSubmit, (errors) => {
        if (Object.keys(errors).length > 0) {
          errorNotification('Please fill in all required fields.');
        }
      })();
    }
  };

  useEffect(() => {
    if (
      !transcodingData?.getVideoTranscodingJobs ||
      transcodingJobIdsRef.current.length === 0
    ) {
      return;
    }

    processTranscodingJobs(transcodingData.getVideoTranscodingJobs);
  }, [transcodingData]);

  useEffect(() => {
    if (transcodingState.error) {
      errorNotification(transcodingState.error);
      setIsUploading(false);
      setUploadProgress(null);
      return;
    }

    if (
      transcodingState.isComplete &&
      transcodingState.pendingUploadResults.length > 0 &&
      transcodingJobIdsRef.current.length > 0
    ) {
      submitDeliverablesAfterTranscoding().catch((error: any) => {
        const errorMessage =
          error?.message || 'Something went wrong. Please try again.';
        errorNotification(errorMessage);

        setIsUploading(false);
        setUploadProgress(null);
        resetTranscodingState();
        setTranscodingState((prev) => ({
          ...prev,
          error: errorMessage,
        }));
      });
    }
  }, [
    transcodingState.isComplete,
    transcodingState.pendingUploadResults,
    transcodingState.error,
    submitDeliverablesAfterTranscoding,
  ]);

  const handleFormSubmit = async (data: ContentFormData) => {
    setIsUploading(true);
    setUploadProgress(null);
    resetTranscodingState();
    setTranscodingState((prev) => ({
      ...prev,
      isUploading: true,
    }));

    try {
      // Process regular content files
      const filesByTask: UploadResult[] = await Promise.all(
        data.content.map(async (content) => {
          return await processAllDeliverableFilesWithTranscoding(
            content.files,
            content.deliverableItemId,
            campaignId,
            createCampaignDeliverableFiles,
            uploadDeliverables,
            uploadDeliverableFileWithConversion,
            apolloClient,
            (fileName, progress, stage) => {
              setUploadProgress({
                fileName,
                progress,
                stage,
              });
            }
          );
        })
      );

      // Process final content files
      const finalContentItems = (data.finalContent ?? []).filter(
        (fc) => fc.files.length > 0
      );
      const finalContentByTask: UploadResult[] = await Promise.all(
        finalContentItems.map(async (fc) => {
          return await processAllDeliverableFilesWithTranscoding(
            fc.files,
            fc.deliverableItemId,
            campaignId,
            createCampaignDeliverableFiles,
            uploadDeliverables,
            uploadDeliverableFileWithConversion,
            apolloClient,
            (fileName, progress, stage) => {
              setUploadProgress({
                fileName,
                progress,
                stage,
              });
            }
          );
        })
      );

      const allUploadResults = [...filesByTask, ...finalContentByTask];
      const allConversionJobIds = allUploadResults
        .flatMap((task) => task.conversionJobIds)
        .filter((id): id is string => Boolean(id));

      if (allConversionJobIds.length > 0) {
        transcodingJobIdsRef.current = allConversionJobIds;
        setTranscodingState({
          isUploading: true,
          progress: { completed: 0, total: allConversionJobIds.length },
          jobIds: allConversionJobIds,
          resultKeys: new Map(),
          error: null,
          pendingUploadResults: allUploadResults,
          isComplete: false,
        });
        setUploadProgress({
          fileName: 'Videos',
          progress: 0,
          stage: 'converting',
        });
      } else {
        // Separate regular content uploads from FILES uploads
        const regularUploads = allUploadResults.filter(
          (result) =>
            !nonApprovedFilesItemIds.has(
              result.submitInput[0]?.deliverableItemId
            )
        );
        const finalContentUploads = allUploadResults.filter((result) =>
          nonApprovedFilesItemIds.has(result.submitInput[0]?.deliverableItemId)
        );

        const contentTasks = buildSubmitInputFromUploadResults(
          regularUploads,
          new Map()
        );

        const { finalItems } = buildFinalSubmissionItems(
          contentTasks,
          data.postCopy
        );

        // Build FILES items for final content
        const finalContentToSubmit = finalContentUploads.map((result) => ({
          deliverableItemId: result.submitInput[0]?.deliverableItemId,
          type: CBDeliverableItemType.FILES as CBDeliverableItemType,
          files: result.submitInput.map((input) => ({
            key: input.key,
            name: input.name,
            type: CampaignMediaType.VIDEO,
            ...(input.videoTranscodingJobId
              ? { videoTranscodingJobId: input.videoTranscodingJobId }
              : {}),
          })),
        }));

        await submitDeliverablesForReviewV2({
          variables: {
            input: {
              offerId: offer?.id ?? '',
              items: [...finalItems, ...finalContentToSubmit],
            },
          },
          awaitRefetchQueries: true,
        });

        successNotification('Deliverables submitted for review successfully.');

        setIsUploading(false);
        setUploadProgress(null);
        resetTranscodingState();

        if (refetch) {
          await refetch();
        }
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || 'Something went wrong. Please try again.';
      errorNotification(errorMessage);

      setIsUploading(false);
      setUploadProgress(null);
      resetTranscodingState();
      setTranscodingState((prev) => ({
        ...prev,
        error: errorMessage,
      }));
    }
  };

  const isSocialMediaPostDeliverable =
    deliverable?.task &&
    'type' in deliverable.task &&
    POST_DELIVERABLE_LINK_TYPES.includes(
      deliverable.task.type as CampaignTaskType
    ) &&
    !deliverableHasContentFileItems(deliverable);

  if (isContentApproved) {
    return (
      <DeliverableSubmitted
        deliverable={deliverable}
        defaultExpanded={false}
        deliverableType="content"
        hasPendingRequest={hasPendingRequest}
        onRequestExtension={onRequestExtension}
        onReviewExtension={onReviewExtension}
      />
    );
  }

  const uploadProgressText =
    uploadProgress?.stage === 'uploading'
      ? `Uploading ${uploadProgress?.fileName}...`
      : `Converting ${uploadProgress?.fileName}...`;

  return (
    <div className={cn(styles.root, className)}>
      {uploadProgress && (
        <div className={styles.progressContainer}>
          <Progress
            percent={uploadProgress.progress}
            status="active"
            format={(percent) => `${percent}%`}
          />
          <p className={styles.progressText}>{uploadProgressText}</p>
        </div>
      )}
      <FormProvider {...methods}>
        <form
          className={cn(styles.form, formClassName)}
          onSubmit={methods.handleSubmit(handleFormSubmit)}
        >
          {!isCollapsed && (
            <>
              {isSocialMediaPostDeliverable ? (
                // For social media post deliverables, only show copy items (as links)
                copies
                  .filter((item) => item.type === CBDeliverableItemType.COPY)
                  .sort((a, b) => {
                    const aIsSubmitted =
                      a.status === CbDeliverableItemV2Status.ON_BRAND_REVIEW ||
                      a.status === CbDeliverableItemV2Status.APPROVED;
                    const bIsSubmitted =
                      b.status === CbDeliverableItemV2Status.ON_BRAND_REVIEW ||
                      b.status === CbDeliverableItemV2Status.APPROVED;

                    if (aIsSubmitted !== bIsSubmitted) {
                      return aIsSubmitted ? -1 : 1;
                    }

                    return (a.task?.orderIdx ?? 0) - (b.task?.orderIdx ?? 0);
                  })
                  .map((item) => (
                    <SubmitCopy
                      key={item.id}
                      item={item}
                      deliverable={deliverable}
                    />
                  ))
              ) : (
                // For content deliverables, show both files and copies
                <>
                  {files
                    .sort((a, b) => {
                      // First, sort by submission status - submitted items go to top
                      const aIsSubmitted =
                        a.status ===
                          CbDeliverableItemV2Status.ON_BRAND_REVIEW ||
                        a.status === CbDeliverableItemV2Status.APPROVED;
                      const bIsSubmitted =
                        b.status ===
                          CbDeliverableItemV2Status.ON_BRAND_REVIEW ||
                        b.status === CbDeliverableItemV2Status.APPROVED;

                      if (aIsSubmitted !== bIsSubmitted) {
                        return aIsSubmitted ? -1 : 1;
                      }

                      // Then sort by orderIdx
                      return (a.task?.orderIdx ?? 0) - (b.task?.orderIdx ?? 0);
                    })
                    .map((item, index) => {
                      const nonApprovedFilesBeforeThis = files
                        .slice(0, index)
                        .filter(
                          (file) =>
                            file.status !== CbDeliverableItemV2Status.APPROVED
                        ).length;

                      return (
                        <SubmitContent
                          key={item.id}
                          item={item}
                          deliverable={deliverable}
                          formIndex={nonApprovedFilesBeforeThis}
                        />
                      );
                    })}
                  {/* Final Content (FILES items) */}
                  {filesItems
                    .filter(
                      (item) =>
                        item.status !== CbDeliverableItemV2Status.APPROVED
                    )
                    .map((item, index) => (
                      <SubmitFinalContent
                        key={item.id}
                        item={item}
                        deliverable={deliverable}
                        formIndex={index}
                      />
                    ))}
                  {copies
                    .filter((item) => item.type === CBDeliverableItemType.COPY)
                    .sort((a, b) => {
                      // First, sort by submission status - submitted items go to top
                      const aIsSubmitted =
                        a.status ===
                          CbDeliverableItemV2Status.ON_BRAND_REVIEW ||
                        a.status === CbDeliverableItemV2Status.APPROVED;
                      const bIsSubmitted =
                        b.status ===
                          CbDeliverableItemV2Status.ON_BRAND_REVIEW ||
                        b.status === CbDeliverableItemV2Status.APPROVED;

                      if (aIsSubmitted !== bIsSubmitted) {
                        return aIsSubmitted ? -1 : 1;
                      }

                      // Then sort by orderIdx
                      return (a.task?.orderIdx ?? 0) - (b.task?.orderIdx ?? 0);
                    })
                    .map((item) => (
                      <SubmitCopy
                        key={item.id}
                        item={item}
                        deliverable={deliverable}
                      />
                    ))}
                </>
              )}
            </>
          )}
          <div className={styles.buttonsContainer}>
            <Button
              type="primary"
              className={styles.submitButton}
              onClick={handleSubmit}
              loading={isUploading}
            >
              {isUploading ? 'Submitting' : 'Submit'}
            </Button>
            {!isCollapsed && (
              <Button
                type="primary"
                onClick={handleToggleCollapse}
                className={styles.collapseButton}
              />
            )}
            {/* Extension Request Buttons */}
            {deliverable?.status !== CbDeliverableV2Status.ON_BRAND_REVIEW &&
              deliverable?.status !== CbDeliverableV2Status.PUBLISHED &&
              hasPendingRequest &&
              onRequestExtension &&
              onReviewExtension && (
                <div className={styles.extensionButtons}>
                  {hasPendingRequest(deliverable.id) ? (
                    <Button
                      type="primary"
                      onClick={() => onReviewExtension(deliverable)}
                    >
                      Review Extension
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      onClick={() => onRequestExtension(deliverable)}
                    >
                      Request Extension
                    </Button>
                  )}
                </div>
              )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default SubmitContentForm;
